import {
  useCallback,
  useEffect,
  useRef,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  type TouchEvent,
  type WheelEvent,
} from 'react';

import type { ViewerPosition } from '../contracts/models';
import '../styles.css';

export interface SvgLayoutDimensions {
  width: number;
  height: number;
}

export interface SvgLayoutViewportProps {
  svgRef: RefObject<SVGSVGElement>;
  groupRef: RefObject<SVGGElement>;
  dimensions: SvgLayoutDimensions;
  position: ViewerPosition;
  scale: number;
  imageUrl?: string;
  flippedHorizontal?: boolean;
  flippedVertical?: boolean;
  cursor?: string;
  viewBoxPadding?: number;
  onMouseDown?: (event: MouseEvent<SVGSVGElement>) => void;
  onClick?: (event: MouseEvent<SVGSVGElement>) => void;
  onMouseMove?: (event: MouseEvent<SVGSVGElement>) => void;
  onMouseLeave?: (event: MouseEvent<SVGSVGElement>) => void;
  onWheel?: (event: WheelEvent<SVGSVGElement>) => void;
  onTouchStart?: (event: TouchEvent<SVGSVGElement>) => void;
  onTouchMove?: (event: TouchEvent<SVGSVGElement>) => void;
  onTouchEnd?: (event: TouchEvent<SVGSVGElement>) => void;
  onImageReady?: () => void;
  children?: ReactNode;
}

export const getFlippedImageTransform = (
  dimensions: SvgLayoutDimensions,
  flippedHorizontal: boolean,
  flippedVertical: boolean,
): string | undefined => {
  const transforms = [
    flippedHorizontal ? `translate(${dimensions.width} 0) scale(-1 1)` : '',
    flippedVertical ? `translate(0 ${dimensions.height}) scale(1 -1)` : '',
  ].filter(Boolean);

  return transforms.length > 0 ? transforms.join(' ') : undefined;
};

/**
 * Shared SVG viewport shell for image-based layouts.
 *
 * Domain layers such as AreaTags, markers, editing handles, and heat-map
 * decorations are passed as children and render inside the transformed group.
 */
export const SvgLayoutViewport = ({
  svgRef,
  groupRef,
  dimensions,
  position,
  scale,
  imageUrl,
  flippedHorizontal = false,
  flippedVertical = false,
  cursor = 'default',
  viewBoxPadding = 40,
  onMouseDown,
  onClick,
  onMouseMove,
  onMouseLeave,
  onWheel,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onImageReady,
  children,
}: SvgLayoutViewportProps) => {
  const readyImageUrlRef = useRef<string | null>(null);
  const readyFrameRef = useRef<number | null>(null);

  const handleImageLoad = useCallback(() => {
    if (!imageUrl || readyImageUrlRef.current === imageUrl) return;
    readyImageUrlRef.current = imageUrl;

    if (readyFrameRef.current !== null) {
      window.cancelAnimationFrame(readyFrameRef.current);
    }
    readyFrameRef.current = window.requestAnimationFrame(() => {
      readyFrameRef.current = window.requestAnimationFrame(() => {
        readyFrameRef.current = null;
        onImageReady?.();
      });
    });
  }, [imageUrl, onImageReady]);

  useEffect(
    () => () => {
      if (readyFrameRef.current !== null) {
        window.cancelAnimationFrame(readyFrameRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !onWheel) return;

    const handleWheel = (event: globalThis.WheelEvent) => {
      onWheel(event as unknown as WheelEvent<SVGSVGElement>);
    };

    svg.addEventListener('wheel', handleWheel, { passive: false });
    return () => svg.removeEventListener('wheel', handleWheel);
  }, [svgRef, onWheel]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const handleTouchStartEvent = (event: globalThis.TouchEvent) => {
      onTouchStart?.(event as unknown as TouchEvent<SVGSVGElement>);
    };
    const handleTouchMoveEvent = (event: globalThis.TouchEvent) => {
      onTouchMove?.(event as unknown as TouchEvent<SVGSVGElement>);
    };
    const handleTouchEndEvent = (event: globalThis.TouchEvent) => {
      onTouchEnd?.(event as unknown as TouchEvent<SVGSVGElement>);
    };

    svg.addEventListener('touchstart', handleTouchStartEvent, { passive: false });
    svg.addEventListener('touchmove', handleTouchMoveEvent, { passive: false });
    svg.addEventListener('touchend', handleTouchEndEvent, { passive: false });

    return () => {
      svg.removeEventListener('touchstart', handleTouchStartEvent);
      svg.removeEventListener('touchmove', handleTouchMoveEvent);
      svg.removeEventListener('touchend', handleTouchEndEvent);
    };
  }, [svgRef, onTouchStart, onTouchMove, onTouchEnd]);

  const paddedWidth = dimensions.width + viewBoxPadding * 2;
  const paddedHeight = dimensions.height + viewBoxPadding * 2;
  const imageTransform = getFlippedImageTransform(
    dimensions,
    flippedHorizontal,
    flippedVertical,
  );

  return (
    <div
      className="iv-layout-viewport__stage"
      style={{ cursor, touchAction: 'none' }}
    >
      <svg
        ref={svgRef}
        viewBox={`${-viewBoxPadding} ${-viewBoxPadding} ${paddedWidth} ${paddedHeight}`}
        onMouseDown={onMouseDown}
        onClick={onClick}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        preserveAspectRatio="xMidYMid meet"
        className="iv-layout-viewport__svg"
      >
        <g
          ref={groupRef}
          transform={`translate(${position.x}, ${position.y}) scale(${scale})`}
        >
          {imageUrl && (
            <image
              href={imageUrl}
              onLoad={handleImageLoad}
              width={dimensions.width}
              height={dimensions.height}
              x="0"
              y="0"
              transform={imageTransform}
            />
          )}
          {children}
        </g>
      </svg>
    </div>
  );
};
